import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import MaskedInput from 'react-text-mask'
import { ssnMask, phoneMask, dateMask } from 'shared/Masks'

const NATIVE_DATE_MIN = '0001-01-01'
const NATIVE_DATE_MAX = '9999-12-31'

const removeFormatting = inputValue =>
  inputValue ? inputValue.replace(/[ ()-/_]/g, '') : inputValue

const toNativeDateValue = rawValue => {
  if (!rawValue && rawValue !== 0) return ''

  const value = String(rawValue).trim()
  if (value === '') return ''

  // Native date value shape used by input[type=date].
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value
  }

  const mmddMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value)
  if (!mmddMatch) return ''

  return `${mmddMatch[3]}-${mmddMatch[1]}-${mmddMatch[2]}`
}

const toExpectedDateValue = rawValue => {
  if (!rawValue && rawValue !== 0) return ''

  const value = String(rawValue).trim()
  if (value === '') return ''

  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (isoMatch) {
    return `${isoMatch[2]}/${isoMatch[3]}/${isoMatch[1]}`
  }

  return value
}

const Input = React.forwardRef(({ isDateMask, ...inputProps }, ref) => (
  <input
    {...inputProps}
    ref={ref}
    type={isDateMask ? 'date' : (inputProps.type || 'text')}
  />
))

const Masked = ({
  nopaste,
  name,
  field = {},
  masktype,
  customChange,
  onBlur,
  ...props
}) => {
  const { onBlur: fieldBlur } = field
  const isDateMask = masktype === 'date'
  const [isDateFocused, setIsDateFocused] = useState(false)
  const [dateDraftValue, setDateDraftValue] = useState('')

  const className = props.invalid
    ? 'masked-input form-control invalid-input is-invalid'
    : 'masked-input form-control'

  const sendChange = nextValue => {
    const event = {
      target: {
        name: field.name || name,
        value: nextValue,
      },
    }
    if (nextValue === '' || field.onChange) {
      field.onChange && field.onChange(event)
    } else if (props.onChange) {
      props.onChange(props.id, nextValue)
    }
  }

  useEffect(() => {
    if (!isDateMask) return
    if (isDateFocused) return

    setDateDraftValue(toNativeDateValue(props.value ?? field.value))
  }, [isDateFocused, isDateMask, props.value, field.value])

  const handleMaskedChange = event => {
    const rawValue = event.target.value
    const nextValue = isDateMask ? toExpectedDateValue(rawValue) : rawValue

    if (isDateMask) {
      setDateDraftValue(rawValue)
      if (rawValue === '' || /^\d{4}-\d{2}-\d{2}$/.test(rawValue)) {
        sendChange(nextValue)
      }
      return
    }

    if (
      removeFormatting(rawValue) ||
      removeFormatting(props.value) ||
      rawValue === ''
    ) {
      sendChange(nextValue)
    }
  }

  const handleChange = event => {
    customChange && typeof customChange === 'function' && customChange(event)
    handleMaskedChange(event)
  }

  const handleBlur = event => {
    if (isDateMask) {
      setIsDateFocused(false)
    }

    if (onBlur && typeof onBlur === 'function') {
      if (!isDateMask) {
        event.target.value = removeFormatting(event.target.value)
      }
      onBlur(event)
    }
    fieldBlur && typeof fieldBlur === 'function' && fieldBlur(event)
  }

  if (isDateMask) {
    return (
      <Input
        {...field}
        {...props}
        isDateMask
        name={field.name || name}
        value={
          isDateFocused
            ? dateDraftValue
            : toNativeDateValue(props.value ?? field.value)
        }
        className={className}
        data-test={`fields.${field.name || name}`}
        min={props.min ?? field.min ?? NATIVE_DATE_MIN}
        max={props.max ?? field.max ?? NATIVE_DATE_MAX}
        onChange={handleChange}
        onFocus={() => setIsDateFocused(true)}
        onBlur={handleBlur}
        onPaste={e => {
          if (nopaste) e.preventDefault()
        }}
      />
    )
  }

  return (
    <MaskedInput
      name={field.name || name}
      {...field}
      {...props}
      invalid={props.invalid ? 'invalid' : ''}
      mask={
        masktype === 'id'
          ? ssnMask
          : masktype === 'phone'
            ? phoneMask
            : dateMask
      }
      render={(ref, inputProps) => (
        <Input {...inputProps} ref={ref} isDateMask={false} />
      )}
      className={className}
      onChange={handleChange}
      data-test={`fields.${field.name || name}`}
      onBlur={handleBlur}
      onPaste={e => {
        if (nopaste) {
          e.preventDefault()
        }
      }}
    />
  )
}

Masked.propTypes = {
  name: PropTypes.string,
  field: PropTypes.object,
  id: PropTypes.string,
  masktype: PropTypes.string,
  customChange: PropTypes.func,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  nopaste: PropTypes.bool,
  invalid: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  min: PropTypes.string,
  max: PropTypes.string,
}

export default Masked
